import { ModalProps } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { ChangePlanEstimateResponse, Subscription } from 'api/billing/types';
import { Loading } from 'components/atoms/loading';
import { Modal } from 'components/atoms/modal';
import {
  getCurrentBillingCycle,
  getPlanPrice,
} from 'components/organisms/module-settings/billing/utils';
import { ReactivateMemberMutationType } from 'queries/billing';

const Description = styled.span`
  font-size: var(--font-size-sm);
  color: var(--gray-80);
`;

const LicenseTitle = styled.p`
  color: var(--gray-70);
  margin-bottom: var(--spacing-02) !important;
`;

const Splitup = styled.p`
  color: var(--gray-50);
  margin-top: var(--spacing-02) !important;
`;

const EstimateDetails = styled.div`
  display: flex;
  align-items: end;
  justify-content: space-between;
  background: var(--green-10);
  border-left: 4px solid var(--green-50);
  padding: var(--spacing-04);
  margin-top: var(--spacing-03) !important;
  font-size: var(--font-size-sm);

  p {
    margin: 0;
  }

  .bold-figure {
    font-weight: var(--font-weight-bold);
    font-size: var(--font-size-base);

    &.invite-count {
      font-size: var(--heading-6-size);
      margin-bottom: var(--spacing-01);
    }
  }

  .billing-cycle {
    color: var(--gray-70);
  }
`;

export const EstimateModal = ({
  userId,
  subscription,
  onOk,
  onCancel,
  estimateMutation,
}: ModalProps & {
  userId: number;
  subscription?: Subscription;
  estimateMutation: ReactivateMemberMutationType;
}) => {
  const [estimate, setEstimate] =
    useState<ChangePlanEstimateResponse | null>(null);
  const { mutateAsync: fetchEstimate, isLoading: isFetchingEstimate } =
    estimateMutation();

  const getEstimate = useCallback(async () => {
    const _estimate = await fetchEstimate(userId);

    setEstimate(_estimate);
  }, [fetchEstimate, userId]);

  useEffect(() => {
    getEstimate();
  }, [getEstimate]);

  const planPrice = useMemo(
    () => (subscription ? getPlanPrice(subscription?.plan.planName) : 0),
    [subscription],
  );

  return (
    <Modal
      visible
      onOk={onOk}
      onCancel={onCancel}
      maskClosable={false}
      title={
        <FormattedMessage
          id="billing.invite.members.estimate.modal.title"
          values={{
            additionalCount: 1,
          }}
        />
      }
      okText={<FormattedMessage id="billing.invite.members.confirm.text" />}
    >
      <Description>
        <FormattedMessage
          id="billing.reactivate.member.estimate.modal.desc"
          values={{
            b: (chunks) => <b>{chunks}</b>,
          }}
        />
      </Description>
      <EstimateDetails>
        <div>
          <LicenseTitle>
            <FormattedMessage id="billing.invite.members.new.license" />
          </LicenseTitle>
          <p className="bold-figure invite-count">{1}</p>
          {planPrice && planPrice > 0 && (
            <Splitup>
              <FormattedMessage
                id="billing.estimate.splitup"
                values={{
                  inviteCount: 1,
                  currencyCode: subscription?.currency,
                  price: planPrice,
                  startDate: moment().format('MMM DD'),
                  endDate: moment(subscription?.endDate).format("MMM DD'YY"),
                }}
              />
            </Splitup>
          )}
        </div>
        <div>
          {isFetchingEstimate ? (
            <Loading size="small" />
          ) : (
            <p className="bold-figure">{`${
              estimate?.amountDue ? estimate.amountDue / 100 : 0
            } ${estimate?.currencyCode}`}</p>
          )}

          <p className="billing-cycle">
            <FormattedMessage
              id="billing.invite.members.billing.cycle"
              values={{
                billingCycle: subscription
                  ? getCurrentBillingCycle(subscription?.plan.planName).replace(
                      '-',
                      '_',
                    )
                  : '',
              }}
            />
          </p>
        </div>
      </EstimateDetails>
    </Modal>
  );
};
